import { Redirect } from 'react-router-dom';
// guards
import AuthGuard from '../guards/AuthGuard';
import OnboardingGuard from '../guards/OnboardingGuard';
// layouts
import DashboardLayout from '../layouts/dashboard';
//
import { PATH_DASHBOARD } from './paths';
import lazy from './lazy';
import useAuth from 'src/hooks/useAuth';
import MarketplaceCareitBadge from 'src/components/marketplace/MarketplaceCareitBadge';
import {
  canUseDistributionAdmin,
  canUseDistributionDriverBoard,
  canUseDistributions,
} from 'src/utils/distributionAccess';

// ----------------------------------------------------------------------

const marketplaceRoute = (importer) => {
  const Component = lazy(importer);

  return function MarketplaceVisibilityRoute(props) {
    const { user } = useAuth();

    if (user?.marketplace_hidden) {
      return <Redirect to={PATH_DASHBOARD.root} />;
    }

    return (
      <>
        <MarketplaceCareitBadge />
        <Component {...props} />
      </>
    );
  };
};

export const canAccessDistributions = (args) => canUseDistributions(args);

export const canAccessDistributionAdmin = (args) =>
  canUseDistributionAdmin(args);

export const canAccessDistributionDriverBoard = (args) =>
  canUseDistributionDriverBoard(args);

// Building happens in a dialog on the Routes page; keep any runId from the link.
const RedirectToRouteBuilder = ({ location }) => {
  const params = new URLSearchParams(location.search);
  params.set('build', '1');
  return (
    <Redirect
      to={`${PATH_DASHBOARD.distributions.runs}?${params.toString()}`}
    />
  );
};

const distributionAdminRoute = (importer) => {
  const Component = lazy(importer);

  return function DistributionAdminVisibilityRoute(props) {
    const { user, isFoodMoverFull, hasHomeDelivery, plan } = useAuth();

    if (
      !canAccessDistributionAdmin({
        user,
        isFoodMoverFull,
        hasHomeDelivery,
        plan,
      })
    ) {
      return <Redirect to={PATH_DASHBOARD.root} />;
    }

    return <Component {...props} />;
  };
};

const distributionDriverRoute = (importer) => {
  const Component = lazy(importer);

  return function DistributionDriverVisibilityRoute(props) {
    const { user, isFoodMoverFull, hasHomeDelivery, plan } = useAuth();

    if (
      !canAccessDistributionDriverBoard({
        user,
        isFoodMoverFull,
        hasHomeDelivery,
        plan,
      })
    ) {
      return <Redirect to={PATH_DASHBOARD.root} />;
    }

    return <Component {...props} />;
  };
};

const DashboardRoutes = {
  path: PATH_DASHBOARD.root,
  guard: AuthGuard,
  layout: DashboardLayout,
  routes: [
    {
      exact: true,
      path: PATH_DASHBOARD.root,
      component: lazy(() => import('../views/Home')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.careitId,
      component: lazy(() => import('../views/CareitId')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.kb,
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.kbArticle(),
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.kbArticleEdit(),
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.kbAdmin,
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.kbAdminArticle(),
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.wiki,
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.wikiArticle(),
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.wikiArticleEdit(),
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.wikiAdmin,
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.wikiAdminArticle(),
      component: lazy(() => import('../views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.chatShortcut(':conversation'),
      component: lazy(() => import('src/views/ChatShortcut')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.reverifyEmailRequest,
      component: lazy(
        () => import('../views/authentication/ResendVerificationEmail')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.reverifyEmail,
      component: lazy(() => import('../views/Home/ReverifyEmail')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.impact,
      component: lazy(() => import('../views/Home/MyImpact')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.incompleteDonations,
      component: lazy(() => import('src/views/Donations/IncompleteDonations')),
    },

    //----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.donationsReport,
      component: lazy(() => import('src/views/DonationsReport')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributionReports,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Reports')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.foodRun.allRuns,
      component: lazy(() => import('src/views/FoodRuns/AllRuns')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.foodRun.myRuns,
      component: lazy(() => import('src/views/FoodRuns/MyRuns')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.foodRun.availableRuns,
      component: lazy(() => import('src/views/FoodRuns/AvailableRuns')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.foodRun.inProgress,
      component: lazy(() => import('src/views/FoodRuns/InProgress')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.foodRun.runHistory,
      component: lazy(() => import('src/views/FoodRuns/RunHistory')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.foodRun.toBeReassigned,
      component: lazy(() => import('src/views/FoodRuns/ToBeReassigned')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.rescues,
      component: lazy(() => import('src/views/Rescues/Rescues')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.myrescues,
      component: lazy(() => import('src/views/Rescues/MyRescues')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.incompleteRescues,
      component: lazy(() => import('src/views/Rescues/Incomplete')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.findDonations,
      component: () => <Redirect to={PATH_DASHBOARD.findItems.donations} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.findItems.donations,
      component: lazy(() => import('src/views/FindDonations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.findItems.marketplace,
      component: marketplaceRoute(() => import('src/views/FindMarketplace')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.donations,
      component: lazy(() => import('src/views/Donations/Donations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.mydonations,
      component: lazy(() => import('src/views/Donations/MyDonations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.myListings,
      component: marketplaceRoute(
        () => import('src/views/Marketplace/MyListings')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.myOrders,
      component: marketplaceRoute(
        () => import('src/views/Marketplace/MyOrders')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.viewOrder(':id'),
      component: marketplaceRoute(
        () => import('src/views/Marketplace/OrderDetails')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.incomingOrders,
      component: marketplaceRoute(
        () => import('src/views/Marketplace/IncomingOrders')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.messages,
      component: marketplaceRoute(
        () => import('src/views/Marketplace/Messages')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.createListing,
      component: marketplaceRoute(
        () => import('src/views/Marketplace/ListingEditor')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.editListing(':id'),
      component: marketplaceRoute(
        () => import('src/views/Marketplace/ListingEditor')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.checkout(':id'),
      component: marketplaceRoute(
        () => import('src/views/Marketplace/Checkout')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.marketplace.viewListing(':id'),
      component: marketplaceRoute(
        () => import('src/views/Marketplace/ListingDetails')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.drivers,
      component: lazy(() => import('src/views/Drivers')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.incomingRequests,
      component: lazy(() => import('src/views/Donations/IncomingRequests')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.viewDonation(':id'),
      component: lazy(() => import('src/views/Donations/Donation')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.viewPartialDonation(':id', ':rescueId'),
      component: lazy(() => import('src/views/Donations/Donation')),
    },

    // ----------------------------------------------------------------------
    {
      exact: true,
      path: PATH_DASHBOARD.business,
      component: lazy(() => import('../views/org/business')),
    },
    // ----------------------------------------------------------------------
    {
      exact: true,
      path: PATH_DASHBOARD.editOrg(':id'),
      component: lazy(() => import('../views/org/EditOrg')),
    },
    // ----------------------------------------------------------------------
    {
      exact: true,
      path: PATH_DASHBOARD.npo,
      component: lazy(() => import('../views/org/npo')),
    },
    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.viewOrg(':id'),
      component: lazy(() => import('src/views/Organization')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.schedule.root,
      component: lazy(() => import('src/views/schedule/Schedule')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.schedule.donations,
      component: lazy(() => import('src/views/schedule/UpcomingDonations')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.teams,
      component: lazy(() => import('src/views/Teams')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.viewTeamMember(':id'),
      component: lazy(() => import('src/views/Teams/TeamMember')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.invitations,
      component: lazy(() => import('src/views/Invitations')),
    },
    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.settings.account(':tab'),
      component: lazy(() => import('src/views/Settings/Account.js')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.settings.account(''),
      component: lazy(() => import('src/views/Settings/Account.js')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.settings.business(':tab'),
      component: lazy(() => import('src/views/Settings/Business.js')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.settings.business(''),
      component: lazy(() => import('src/views/Settings/Business.js')),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.settings.documents,
      component: lazy(() => import('src/views/Documents')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.settings.tags,
      component: lazy(() => import('src/views/Settings/Tags')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.vehicle,
      component: lazy(() => import('src/views/Vehicle')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.customizeEmail,
      component: lazy(() => import('src/views/CustomizeEmail')),
    },

    // ----------------------------------------------------------------------
    {
      exact: true,
      path: PATH_DASHBOARD.editRecurringFoodRun(':id'),
      component: lazy(
        () => import('src/views/Donations/EditRecurringDonations.js')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.editRecurringDonation(':id'),
      component: lazy(
        () => import('src/views/Donations/EditRecurringDonations.js')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.editDonationSingleInstance(':id'),
      component: lazy(
        () => import('src/views/Donations/EditSingleInstanceDonation.js')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.cancellationReport,
      component: lazy(() => import('src/views/Donations/CancellationReport')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.taxReceipts,
      component: lazy(() => import('src/views/TaxReceipts')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.foodMoverTaxReceipts,
      component: lazy(() => import('src/views/FoodMoverTaxReceipts')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.taxReceiptsRedirect,
      component: lazy(() => import('src/views/TaxReceiptsRedirect')),
      // Note: path is /go-to-tax-receipts to avoid matching /tax-receipts first in permissions
    },
    {
      exact: true,
      path: PATH_DASHBOARD.editFoodRunSingleInstance(':id'),
      component: lazy(
        () => import('src/views/FoodRun/EditFoodRunSingleInstance.js')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.viewFoodRun(':id'),
      component: lazy(() => import('src/views/FoodRun/FoodRunDetails.js')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.allPlans,
      component: lazy(() => import('src/views/Pricing')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.subscriptionComplete,
      component: lazy(() => import('src/views/SubscriptionComplete')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.admin.overview,
      component: lazy(() => import('src/views/admin/Overview')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.users,
      component: lazy(() => import('src/views/admin/Users')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.orgs,
      component: lazy(() => import('src/views/admin/Organizations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.outreach,
      component: lazy(() => import('src/views/admin/OrganizationMap')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.chats,
      component: lazy(() => import('src/views/admin/Chats')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.sms,
      component: lazy(() => import('src/views/admin/SmsMessages')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.freshcueNotifications,
      component: lazy(
        () => import('src/views/admin/FreshcueNotificationMonitor')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.freshcueWaitlist,
      component: lazy(() => import('src/views/admin/FreshcueWaitlist')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.approvalQueue,
      component: lazy(() => import('src/views/admin/ApprovalQueue')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.orgUpdateQueue,
      component: lazy(() => import('src/views/admin/OrgUpdateQueue')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.onboardingIntakeQueue,
      component: lazy(() => import('src/views/admin/OnboardingIntakeQueue')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.complaintsQueue,
      component: lazy(() => import('src/views/admin/ComplaintsQueue')),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.admin.map,
      component: lazy(() => import('src/views/admin/OrganizationMap')),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.admin.subscriptions,
      component: lazy(() => import('src/views/admin/Subscriptions')),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.admin.licensing.overview,
      component: lazy(
        () => import('src/views/admin/licensing/LicensingOverview')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.licensing.clients,
      component: lazy(
        () => import('src/views/admin/licensing/LicensingClients')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.licensing.clientPattern,
      component: lazy(
        () => import('src/views/admin/licensing/LicensingClient')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.licensing.map,
      component: lazy(
        () => import('src/views/admin/licensing/LicensingCoverageMap')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.licensing.schedule,
      component: lazy(
        () => import('src/views/admin/licensing/LicensingSchedule')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.brands,
      component: lazy(() => import('src/views/admin/Brands')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newBrand,
      component: lazy(() => import('src/views/admin/BrandEditor')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.brandPattern,
      component: lazy(() => import('src/views/admin/BrandEditor')),
    },
    // ── Newsletters ────────────────────────────────────────────
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.root,
      component: lazy(() => import('src/views/admin/Newsletters/Campaigns')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.subscribers,
      component: lazy(() => import('src/views/admin/Newsletters/Subscribers')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.groups,
      component: lazy(() => import('src/views/admin/Newsletters/Groups')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.templates,
      component: lazy(() => import('src/views/admin/Newsletters/Templates')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.deliverability,
      component: lazy(
        () => import('src/views/admin/Newsletters/Deliverability')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.automations,
      component: lazy(() => import('src/views/admin/Newsletters/Automations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.automation(),
      component: lazy(
        () => import('src/views/admin/Newsletters/AutomationDetail')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.activity,
      component: lazy(() => import('src/views/admin/Newsletters/Activity')),
    },
    // One route for new and edit: the first save moves /new to /:id/edit,
    // and a separate route would remount the editor (losing the step, tab
    // and open dialogs).
    {
      exact: true,
      path: [
        PATH_DASHBOARD.admin.newsletters.create,
        PATH_DASHBOARD.admin.newsletters.edit(),
      ],
      component: lazy(
        () => import('src/views/admin/Newsletters/CampaignEditor')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.newsletters.detail(),
      component: lazy(
        () => import('src/views/admin/Newsletters/CampaignDetail')
      ),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.admin.duplicateOrgs,
      component: lazy(() => import('src/views/admin/DuplicateOrganizations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.mergeOrgs,
      component: lazy(() => import('src/views/admin/MergeOrganizations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.mergeUsers,
      component: lazy(() => import('src/views/admin/MergeUsers')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.createOrg,
      component: lazy(() => import('src/views/admin/CreateOrganization')),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.admin.viewUser(':id'),
      component: lazy(() => import('src/views/admin/User')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.viewOrg(':id'),
      component: lazy(() => import('src/views/admin/Organization')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.trainingEditor,
      component: lazy(() => import('src/views/TrainingEditor')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.admin.executiveUsers,
      component: lazy(() => import('src/views/admin/ExecutiveUsers')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.viewExecutiveUsers(':id'),
      component: lazy(() => import('src/views/admin/ExecutiveUser')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.admin.corporateAccounts,
      component: lazy(() => import('src/views/admin/CorporateAccounts')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.viewCorporateAccount(':id'),
      component: lazy(() => import('src/views/admin/CorporateAccount')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.admin.imports.donations,
      component: lazy(() => import('src/views/Import/Donations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.imports.users,
      component: lazy(() => import('src/views/Import/Users')),
    },

    {
      exact: true,
      path: [
        PATH_DASHBOARD.admin.deletions.donations,
        PATH_DASHBOARD.admin.deletions.foodRuns,
        PATH_DASHBOARD.admin.deletions.orgs,
      ],
      component: lazy(() => import('src/views/Deletions/DeletedRecords')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.admin.inventory,
      component: lazy(() => import('src/views/admin/Inventory')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.distributions.root,
      component: lazy(
        () => import('src/views/admin/distributions/DistributionsMonitor')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.distributions.orgPattern,
      component: lazy(
        () => import('src/views/admin/distributions/DistributionOrgActivity')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.cancellations,
      component: lazy(() => import('src/views/admin/Cancellations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.einStatus,
      component: lazy(() => import('src/views/admin/EinStatus')),
    },
    {
      exact: true,
      path: '/dashboard/admin/ein-verification',
      component: () => <Redirect to={PATH_DASHBOARD.admin.einStatus} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.marketplace,
      component: () => (
        <Redirect to={PATH_DASHBOARD.admin.marketplaceGeneral} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.marketplaceGeneral,
      component: lazy(() => import('src/views/admin/marketplace/General')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.marketplaceSales,
      component: lazy(() => import('src/views/admin/marketplace/Sales')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.marketplaceListings,
      component: lazy(() => import('src/views/admin/marketplace/Listings')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.marketplaceSellers,
      component: lazy(() => import('src/views/admin/marketplace/Sellers')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.marketplaceSettings,
      component: lazy(() => import('src/views/admin/marketplace/Settings')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.marketplaceReconciliation,
      component: lazy(
        () => import('src/views/admin/marketplace/Reconciliation')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.viewInventory(':id'),
      component: lazy(() => import('src/views/admin/InventoryItem')),
    },

    // ── CRM ────────────────────────────────────────────────────

    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.root,
      component: lazy(() => import('src/views/admin/CRM/Dashboard')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.pipeline(),
      component: lazy(() => import('src/views/admin/CRM/Pipeline')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.deal(),
      component: lazy(() => import('src/views/admin/CRM/DealDetail')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.contacts,
      component: lazy(() => import('src/views/admin/CRM/Contacts')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.contactDetail(),
      component: lazy(() => import('src/views/admin/CRM/ContactDetail')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.companies,
      component: lazy(() => import('src/views/admin/CRM/Companies')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.company(),
      component: lazy(() => import('src/views/admin/CRM/CompanyDetail')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.summary,
      component: lazy(() => import('src/views/admin/CRM/Summary')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.calendar,
      component: lazy(() => import('src/views/admin/CRM/Calendar')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.auditLog,
      component: lazy(() => import('src/views/admin/CRM/AuditLog')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.import,
      component: lazy(() => import('src/views/admin/CRM/Import')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.settings,
      component: lazy(() => import('src/views/admin/CRM/Settings')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.activities,
      component: lazy(() => import('src/views/admin/CRM/Tasks')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.leads,
      component: lazy(() => import('src/views/admin/CRM/Leads')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.prospecting,
      component: lazy(() => import('src/views/admin/CRM/Prospecting')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.leadDetail(),
      component: lazy(() => import('src/views/admin/CRM/LeadDetail')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.adminUsers,
      component: lazy(() => import('src/views/admin/AdminUsers')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.mobileApps,
      component: lazy(() => import('src/views/admin/MobileAppMinBuilds')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.kb,
      component: lazy(() => import('src/views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.kbArticle(),
      component: lazy(() => import('src/views/KnowledgeBase')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.tasks,
      component: lazy(() => import('src/views/admin/CRM/Tasks')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.admin.crm.task(),
      component: lazy(() => import('src/views/admin/CRM/TaskDetail')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.executive.orgSettings,
      component: lazy(() => import('../views/ExecAndCorpOrgForm')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.home,
      component: lazy(() => import('../views/ExecutiveDashboard/Home')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.onboardingRequests,
      guard: OnboardingGuard,
      component: lazy(
        () => import('../views/ExecutiveDashboard/OnboardingIntakeRequests')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.donations.all,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Donations/All')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.donations.incoming,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Donations/Incoming')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.donations.internal,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Donations/Internal')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.donations.outgoing,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Donations/Outgoing')
      ),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.executive.orgs,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Organizations')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.viewOrg(':id'),
      component: lazy(
        () => import('../views/ExecutiveDashboard/Organizations/Organization')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.createOrg,
      component: lazy(
        () => import('../views/ExecutiveDashboard/CreateOrganization')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.geozones,
      component: lazy(() => import('../views/ExecutiveDashboard/Geozones')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.penalties.log,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Penalties/index')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.penalties.recommended,
      component: lazy(
        () =>
          import('../views/ExecutiveDashboard/Penalties/PenaltyRecommendedReport')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.penalties.history,
      component: lazy(
        () =>
          import('../views/ExecutiveDashboard/Penalties/PenaltyHistoryReport')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.contactSummary,
      component: lazy(
        () => import('../views/ExecutiveDashboard/ContactSummary')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.complianceSummary,
      component: lazy(
        () => import('../views/ExecutiveDashboard/ComplianceSummary')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.contactLog,
      component: lazy(() => import('../views/ExecutiveDashboard/ContactLog')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.activityLog,
      component: lazy(() => import('../views/ExecutiveDashboard/ActivityLog')),
    },

    // Scheduling routes
    {
      exact: true,
      path: PATH_DASHBOARD.executive.scheduling.appointments,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Scheduling/AppointmentsList')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.scheduling.appointmentNew,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Scheduling/AppointmentForm')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.scheduling.appointmentView(':id'),
      component: lazy(
        () => import('../views/ExecutiveDashboard/Scheduling/AppointmentDetail')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.scheduling.appointmentEdit(':id'),
      component: lazy(
        () => import('../views/ExecutiveDashboard/Scheduling/AppointmentForm')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.scheduling.calendar,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Scheduling/Calendar')
      ),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.corporate.home,
      component: lazy(() => import('src/views/CorporateDashboard/Home')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.donations,
      component: lazy(() => import('src/views/CorporateDashboard/Donations')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.schedule,
      component: lazy(() => import('src/views/CorporateDashboard/Schedule')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.viewDonation(':id'),
      component: lazy(() => import('src/views/CorporateDashboard/Donation')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.recipients,
      component: lazy(() => import('src/views/CorporateDashboard/NPOs')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.teams,
      component: lazy(() => import('src/views/CorporateDashboard/Teams')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.viewTeam(':id'),
      component: lazy(() => import('src/views/CorporateDashboard/Team')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.report,
      component: lazy(() => import('src/views/CorporateDashboard/Report')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.inventory,
      component: lazy(
        () => import('src/views/CorporateDashboard/Inventory/Inventory')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.wasteTracking,
      component: lazy(() => import('src/views/WasteTracking')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.wasteReport,
      component: lazy(() => import('src/views/CorporateDashboard/WasteReport')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.cancellationsReport,
      component: lazy(
        () => import('src/views/CorporateDashboard/CancellationsReport')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.nonDonatingLocationsReport,
      component: lazy(
        () => import('src/views/CorporateDashboard/NonDonatingLocationsReport')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.donorTeamOnboardingReport,
      component: lazy(
        () => import('src/views/CorporateDashboard/DonorTeamOnboardingReport')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.taxReceipts,
      component: lazy(() => import('src/views/CorporateDashboard/TaxReceipts')),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.root,
      guard: OnboardingGuard,
      component: lazy(() => import('src/views/CorporateDashboard/Onboarding')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.create(':profileId').form,
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Create/Form')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.create(':profileId').agreement,
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Create/Agreement')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.create(':profileId').document,
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Create/Document')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.create(':profileId').upload,
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Create/Upload')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.edit.form(':id'),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Edit/Form')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.edit.agreement(':id'),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Edit/Agreement')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.edit.document(':id'),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Edit/Document')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.edit.upload(':id'),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Edit/Upload')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.submissions.form(':id'),
      guard: OnboardingGuard,
      component: lazy(
        () =>
          import('src/views/CorporateDashboard/Onboarding/Submissions/FormSubmissions')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.requests,
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Requests')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.intakeQueue,
      guard: OnboardingGuard,
      component: lazy(
        () => import('../views/ExecutiveDashboard/OnboardingIntakeRequests')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.request(':id', ':teamId'),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/Request')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.progress.form(':id', ':teamId'),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/View/Form')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.progress.agreement(
        ':id',
        ':teamId'
      ),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/View/Agreement')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.progress.document(
        ':id',
        ':teamId'
      ),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/View/Document')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.progress.upload(
        ':id',
        ':teamId'
      ),
      guard: OnboardingGuard,
      component: lazy(
        () => import('src/views/CorporateDashboard/Onboarding/View/Upload')
      ),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.complianceProfile.root,
      guard: OnboardingGuard,
      component: lazy(
        () =>
          import('src/views/CorporateDashboard/Onboarding/ComplianceProfile/All')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.complianceProfile.create,
      guard: OnboardingGuard,
      component: lazy(
        () =>
          import('src/views/CorporateDashboard/Onboarding/ComplianceProfile/Create')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.complianceProfile.view(':id'),
      guard: OnboardingGuard,
      component: lazy(
        () =>
          import('src/views/CorporateDashboard/Onboarding/ComplianceProfile/View')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporate.onboarding.complianceProfile.edit(':id'),
      guard: OnboardingGuard,
      component: lazy(
        () =>
          import('src/views/CorporateDashboard/Onboarding/ComplianceProfile/Edit')
      ),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.corporate.orgSettings,
      component: lazy(() => import('src/views/ExecAndCorpOrgForm')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.donorOnboarding,
      component: lazy(() => import('src/views/CorporateOnboarding/Donor')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.requests,
      component: lazy(() => import('src/views/CorporateOnboarding/Donor')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.request(':id', ':gatekeeperId'),
      component: lazy(() => import('src/views/CorporateOnboarding/Request')),
    },
    {
      exact: true,
      path: '/dashboard/corp-onboarding/corp/:id/:gatekeeperId',
      component: lazy(() => import('src/views/CorporateOnboarding/Request')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.progress(':id', ':teamId'),
      component: lazy(() => import('src/views/CorporateOnboarding/Progress')),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.complete.form(':id'),
      component: lazy(
        () => import('src/views/CorporateOnboarding/Complete/Form')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.complete.agreement(':id'),
      component: lazy(
        () => import('src/views/CorporateOnboarding/Complete/Agreement')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.complete.document(':id'),
      component: lazy(
        () => import('src/views/CorporateOnboarding/Complete/Document')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.complete.upload(':id'),
      component: lazy(
        () => import('src/views/CorporateOnboarding/Complete/Upload')
      ),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.view.form(
        ':npoTeamId',
        ':itemId'
      ),
      component: lazy(() => import('src/views/CorporateOnboarding/View/Form')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.view.agreement(
        ':npoTeamId',
        ':itemId'
      ),
      component: lazy(
        () => import('src/views/CorporateOnboarding/View/Agreement')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.view.document(
        ':npoTeamId',
        ':itemId'
      ),
      component: lazy(
        () => import('src/views/CorporateOnboarding/View/Document')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.corporateOnboarding.view.upload(
        ':npoTeamId',
        ':itemId'
      ),
      component: lazy(
        () => import('src/views/CorporateOnboarding/View/Upload')
      ),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.agreement(':id'),
      component: lazy(() => import('src/views/Partnerships/Agreement')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.agreements,
      component: lazy(() => import('src/views/Partnerships/Agreements')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.partnerships,
      component: lazy(() => import('src/views/Partnerships')),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.all,
      component: lazy(() => import('../views/ExecutiveDashboard/Inspections')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.mine,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Inspections/Mine')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.incomplete,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Inspections/Incomplete')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.view(':id'),
      component: lazy(
        () => import('../views/ExecutiveDashboard/common/DetailView')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.fro,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Inspections/Forms/FRO')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.efg,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Inspections/Forms/EFG')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.reports.fro,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Inspections/Reports/FRO')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.inspections.reports.efg,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Inspections/Reports/EFG')
      ),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.executive.complaints.all,
      component: lazy(() => import('../views/ExecutiveDashboard/Complaints')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.complaints.mine,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Complaints/Mine')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.complaints.incomplete,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Complaints/Incomplete')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.complaints.view(':id'),
      component: lazy(
        () => import('../views/ExecutiveDashboard/common/DetailView')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.complaints.form,
      component: lazy(
        () => import('../views/ExecutiveDashboard/Complaints/Forms/Complaint')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.complaints.report,
      component: lazy(
        () =>
          import('../views/ExecutiveDashboard/Complaints/Reports/Complaints')
      ),
    },

    {
      exact: true,
      path: PATH_DASHBOARD.executive.nov.recommendedReport,
      component: lazy(
        () => import('../views/ExecutiveDashboard/NOV/Reports/Recommended')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.executive.nov.issuedReport,
      component: lazy(
        () => import('../views/ExecutiveDashboard/NOV/Reports/Issued')
      ),
    },

    // ----------------------------------------------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.freshcue.allPosts,
      component: lazy(() => import('../views/FreshcueDashboard/AllPosts')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.freshcue.postDetails(':postId'),
      component: lazy(() => import('../views/FreshcueDashboard/PostDetails')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.freshcue.businessPosts,
      component: lazy(() => import('../views/FreshcueDashboard/BusinessPosts')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.freshcue.businessReservations,
      component: lazy(
        () => import('../views/FreshcueDashboard/BusinessReservations')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.freshcue.manageMembers,
      component: lazy(() => import('../views/FreshcueDashboard/ManageMembers')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.freshcue.donors,
      component: lazy(() => import('../views/FreshcueDashboard/ProgramDonors')),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.freshcue.donorStats,
      component: lazy(() => import('../views/FreshcueDashboard/DonorStats')),
    },

    // ----- Distributions (Home Delivery) ----------------------------------

    {
      exact: true,
      path: PATH_DASHBOARD.distributions.root,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/DistributionsDashboard')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.programs,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Programs')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.program(':id'),
      component: distributionAdminRoute(
        () => import('src/views/Distributions/ProgramDetail')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.pickupSites,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/PickupSites')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.holidays,
      // Service calendar moved to org settings (shared with other products).
      component: () => (
        <Redirect to={PATH_DASHBOARD.settings.business('service_calendar')} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.addressAudit,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/AddressAudit')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.clients,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Clients')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.client(':id'),
      component: distributionAdminRoute(
        () => import('src/views/Distributions/ClientDetail')
      ),
    },
    {
      exact: true,
      // Addresses are managed from clients; the old list goes to Clients.
      path: PATH_DASHBOARD.distributions.households,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Recipients')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.household(':id'),
      component: distributionAdminRoute(
        () => import('src/views/Distributions/HouseholdDetail')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.members,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Members')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.items,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Items')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.recipients,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Recipients')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.settings,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Settings')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.schedules,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Schedules')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.standingRoutes,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/StandingRoutes')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.runBuilder,
      component: RedirectToRouteBuilder,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.runs,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Runs')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyDriverBoard,
      component: () => (
        <Redirect to={PATH_DASHBOARD.distributions.availableRoutes} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.availableRoutes,
      component: distributionDriverRoute(
        () => import('src/views/Distributions/AvailableRoutes')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.myRoutes,
      component: distributionDriverRoute(
        () => import('src/views/Distributions/MyRoutes')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.requests,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Requests')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.deliveries,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/Deliveries')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.prep,
      component: distributionAdminRoute(
        () => import('src/views/Distributions/HubPrep')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.run(':id'),
      component: distributionDriverRoute(
        () => import('src/views/Distributions/RunDetail')
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyPickupSites,
      component: () => (
        <Redirect to={PATH_DASHBOARD.distributions.pickupSites} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyHouseholds,
      component: () => (
        <Redirect to={PATH_DASHBOARD.distributions.households} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyHousehold(':id'),
      component: ({ match }) => (
        <Redirect
          to={PATH_DASHBOARD.distributions.household(match.params.id)}
        />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyMembers,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.members} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyRecipientRequests,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.requests} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyItems,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.items} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacySchedules,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.schedules} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacySettingsSchedules,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.schedules} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyRuns,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.runs} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyRun(':id'),
      component: ({ match }) => (
        <Redirect to={PATH_DASHBOARD.distributions.run(match.params.id)} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyServiceDayRun(':id'),
      component: ({ match }) => (
        <Redirect to={PATH_DASHBOARD.distributions.run(match.params.id)} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyAvailableRoutes,
      component: () => (
        <Redirect to={PATH_DASHBOARD.distributions.availableRoutes} />
      ),
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyMyRoutes,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.myRoutes} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyRunBuilder,
      component: RedirectToRouteBuilder,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyRequests,
      component: () => <Redirect to={PATH_DASHBOARD.distributions.requests} />,
    },
    {
      exact: true,
      path: PATH_DASHBOARD.distributions.legacyReports,
      component: () => <Redirect to={PATH_DASHBOARD.distributionReports} />,
    },

    // ----------------------------------------------------------------------

    {
      component: () => <Redirect to="/404" />,
    },
  ],
};

export default DashboardRoutes;
